'use client';
import { startHolyLoader } from 'holy-loader';
import debounce from 'lodash.debounce';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CiSearch } from 'react-icons/ci';

import { getCookie } from '@/lib/cookies';
import { eventTracker } from '@/lib/service-workers';

import Container from '@/components/@base/container';
import { CONTAINER_TAG_TYPE } from '@/components/@base/container/type';
import { Input } from '@/components/@base/input';
import { useGetProfile } from '@/components/layout/services/hooks';
import { useGetSearch } from '@/components/shared/services/hooks';

import useCommonModalStore from '@/store/common-modal-store';
import { useSearchStore } from '@/store/search.store';

import { DATA_CY_SEARCH_POP_UP_INPUT } from '@/constant/cypress';
import ICON_SIZE, { ICON_COLOR } from '@/constant/icon-size-color';
import { pageLevelLocalization } from '@/constant/localization';

const { layout: layoutLocalization } = pageLevelLocalization;

export default function SearchField() {
  const searchParams = useSearchParams();
  const [value, setValue] = useState('');
  const { refetch } = useGetSearch({
    params: { find: value, with_demo: true, sellable: true },
    enabled: true,
  });
  const { setParams } = useSearchStore();
  const { setShow } = useCommonModalStore();
  const { profile } = useGetProfile();
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const debouncedRefetch = useCallback(
    debounce((value) => {
      refetch();
      setParams(value);
      eventTracker(profile?.mobile, 'search_query', new Date().toISOString(), {
        find: value,
      });

      const isExistGA4Cookie = getCookie('cookie_consent') === 'accepted';
      if (isExistGA4Cookie || process.env.NEXT_PUBLIC_DISABLE_COOKIE_PERMISSION === 'true') {
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'search', {
            search_term: value,
          });
        }
      }
    }, 1000),
    [refetch, setParams],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      debouncedRefetch.cancel();
      startHolyLoader();
      setShow(false);
      router.push(`/search?find=${encodeURIComponent(value)}`);
    }
  };

  useEffect(() => {
    const findParam = searchParams.get('find') || '';
    setValue(findParam);
    if (findParam) {
      setParams(findParam);
      refetch();
    }
  }, [searchParams, setParams, refetch]);

  useEffect(() => {
    inputRef?.current?.focus();
    if (value) {
      debouncedRefetch(value);
    }
    return () => {
      debouncedRefetch.cancel();
    };
  }, [value, debouncedRefetch]);

  useEffect(() => {
    if (value === '') {
      setParams('');
    }
  }, [value, setParams]);

  return (
    <Input
      data-cy={DATA_CY_SEARCH_POP_UP_INPUT}
      placeholder={layoutLocalization.forExampleWoodmart}
      containerClassName='border-[#E5E8EB] h-[68px] border shadow-[0px_2px_8px_0px_rgba(73,75,84,0.05)] text-base rounded-lg w-full max-w-[640px]'
      className='border-none'
      value={value}
      ref={inputRef}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      PlaceholderIcon={() => (
        <Container type={CONTAINER_TAG_TYPE.SPAN} className='pr-[22px]'>
          <CiSearch size={ICON_SIZE.lg} color={ICON_COLOR.gray} strokeWidth={0.5} />
        </Container>
      )}
    />
  );
}
